// Shared between Community, Corporate, etc
// NOTE: Search for all usages of this file (ie shared.min.js) when changed to ensure ?v=XX is bumped where needed

const ANALYTICS_ERROR_LOGGING_STORAGE_KEY = 'rv.analyticsErrorLoggingEnabled';

function setAnalyticsErrorLogging(enabled) {
    localStorage.setItem(ANALYTICS_ERROR_LOGGING_STORAGE_KEY, Boolean(enabled).toString());
}

function logAnalyticsError(error) {
    if (localStorage.getItem(ANALYTICS_ERROR_LOGGING_STORAGE_KEY) === 'true') {
        console.error(error);
    }
    logToServer(error, null, 'Analytics tracking error', 'SEVERE');
}

/**
 * Logs an error to a remote error-handling endpoint.
 *
 * @param {Error|string} error - The error object or string to be logged.
 * @param {Object} info - Additional information related to the error context.
 * @param {string} [message=''] - Optional human-readable message to supplement the error log.
 * @param {'INFO'|'WARNING'|'SEVERE'} [level='INFO'] - Optional logging level indicating the severity of the error.
 */
function logToServer(error, info, message = '', level = 'INFO') {
    fetch(new Request('/rapi/error-handler', {
        method: 'POST',
        body: JSON.stringify({
            error: error?.stack ?? error?.message ?? error ?? null,
            info,
            message,
            level
        }),
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
        }
    })).catch(
        // Ignore. Kinda hard to log the failure to log.
    );
}

// Very basic debounce functionality
function debounce(func, delayMillis) {
    let timeoutId;
    return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delayMillis);
    };
}

function generateUuid() {
    // Note: crypto.randomUUID() is not available in non-secure context (eg no SSL, most often seen in local development or QA/pre-QA).
    if (typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    const arr = new Uint8Array(16);
    crypto.getRandomValues(arr);
    return [...arr].map(b => b.toString(16).padStart(2, '0')).join('');
}

//region visitdata custom code
const VISIT_DATA_UUID_STORAGE_KEY = 'vsd-uuid-key';
const VISIT_DATA_SESSION_STORAGE_KEY = 'vsd-key';
const THIRTY_MINUTES_MILLIS = 1_000 * 60 * 30;

function shouldUpdateVisitData(existingVisitData, newVisitData) {
    // This tracks if internal links to other pages within the site trigger a "referral"
    const selfReferral = newVisitData.medium === 'referral' && newVisitData.source === visitData.exportedForTesting.getDomain_(window.location.origin);
    if (selfReferral) {
        // Retain the original source/medium data in a self-referral scenario
        newVisitData.source = existingVisitData?.source ?? '(unknown)';
        newVisitData.medium = existingVisitData?.medium ?? '(unknown)';
    }
    // We either don't already have a source/medium, or the new source is different and NOT "direct" (eg, they came from some other source)
    return existingVisitData?.source == null || existingVisitData?.medium == null || (newVisitData.source !== '(direct)' && newVisitData.source !== existingVisitData.source);
}

function setVisitData() {
    visitData.setOption('cache', false); // No caching via visitData, since we need to update session source in certain scenarios

    const currentVisitData = visitData.get();
    const currentRawData = visitData.rawData();
    const now = Date.now();

    let visitorUuid = localStorage.getItem(VISIT_DATA_UUID_STORAGE_KEY);
    // If we don't have a visitor UUID yet, this is the first tracked visit.
    if (visitorUuid == null) {
        visitorUuid = generateUuid();
        localStorage.setItem(VISIT_DATA_UUID_STORAGE_KEY, visitorUuid);
    }

    // Using localStorage for "session" in order to persist more in-line with a first-party cookie (how GA4 does it).
    const sessionData = JSON.parse(localStorage.getItem(VISIT_DATA_SESSION_STORAGE_KEY)) ?? {};
    const dataUpdateRequired = shouldUpdateVisitData(sessionData, currentVisitData);
    if (dataUpdateRequired || sessionData.lastHit == null || (now - sessionData.lastHit > THIRTY_MINUTES_MILLIS)) {
        // Either the first hit or it's been more than thirty minutes or there's a new non-direct source--set a new session UUID
        sessionData.uuid = generateUuid();
        Object.assign(sessionData, currentVisitData);
        sessionData.rawData = currentRawData;
    }
    // Always update the "last hit" time
    sessionData.lastHit = now;

    localStorage.setItem(VISIT_DATA_SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    return {
        visitorUuid,
        sessionData,
    };
}

// 15-second debouncing so we don't continuously fire during scrolling, mouse moving, etc.
const debouncedSetVisitData = debounce(setVisitData, 15_000);

function initializeVisitData() {
    setVisitData(); // Initial visitData population.

    //region Handle various events for session keep-alive.
    window.addEventListener('scroll', debouncedSetVisitData, {passive: true});

    ['mousemove', 'keydown', 'click'].forEach(event =>
        document.addEventListener(event, debouncedSetVisitData)
    );

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            // No need to use the debounced version here, since this event is not frequently triggered.
            setVisitData();
        }
    });
    //endregion
}

function submitVisitData(visitData, eventType) {
    if (['engaged_visit', 'highly_engaged_visit'].includes(eventType)) {
        fetch(new Request('/rapi/visit-data', {
            method: 'POST',
            body: JSON.stringify({...visitData, eventType}),
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        }));
    }
}

//endregion